import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { FaqItem } from "../components/landing/FaqItem";
import "../styles/landing.css";
import {
  Thermometer, ShoppingCart, ShoppingCart as ShoppingCartIcon, Users, Bot, Store, ArrowRight, Check,
  Shield, Clock, Zap, BarChart3, ChevronRight, Star, Sparkles,
  ClipboardCheck, FileText, Bell, Calendar, ChefHat, Package,
  Quote, MessageCircle, HelpCircle, Mic, Send, Lock,
  Smartphone, TrendingUp, UserCheck, FolderOpen, MapPin
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Scroll Animation Hook ───
const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

// ─── Animated Counter ───
const Counter = ({ end, suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [ref, visible] = useScrollReveal();
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [visible, end, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
};

// ─── Section Wrapper with reveal ───
const RevealSection = ({ children, className = "", delay = 0 }) => {
  const [ref, visible] = useScrollReveal();
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

// ─── Custom SVG Icons ───
const HaccpIcon = ({ size = 28 }) => (
  <svg fill="none" width={size} height={size} viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="grad-haccp" x1="1.5" x2="1.5" y1="1" y2="15" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#e100ff"/>
        <stop offset="1" stopColor="#7f00ff"/>
      </linearGradient>
    </defs>
    <g fill="url(#grad-haccp)">
      <path clipRule="evenodd" d="m6.5 6.5c0-.27614.22386-.5.5-.5h3c.2761 0 .5.22386.5.5s-.2239.5-.5.5h-3c-.27614 0-.5-.22386-.5-.5z" fillRule="evenodd"/>
      <path clipRule="evenodd" d="m3.5 2.5c0-.82843.67157-1.5 1.5-1.5h4c.82843 0 1.5.67157 1.5 1.5v.5c0 .82843-.67157 1.5-1.5 1.5h-4c-.82843 0-1.5-.67157-1.5-1.5zm1.5-.5c-.27614 0-.5.22386-.5.5v.5c0 .27614.22386.5.5.5h4c.27614 0 .5-.22386.5-.5v-.5c0-.27614-.22386-.5-.5-.5z" fillRule="evenodd"/>
      <path d="m5 6.5c0 .27614-.22386.5-.5.5s-.5-.22386-.5-.5.22386-.5.5-.5.5.22386.5.5z"/>
      <path clipRule="evenodd" d="m4.5 6.25c-.13807 0-.25.11193-.25.25s.11193.25.25.25.25-.11193.25-.25-.11193-.25-.25-.25zm-.75.25c0-.41421.33579-.75.75-.75s.75.33579.75.75-.33579.75-.75.75-.75-.33579-.75-.75z" fillRule="evenodd"/>
      <path clipRule="evenodd" d="m6.5 9c0-.27614.22386-.5.5-.5h3c.2761 0 .5.22386.5.5s-.2239.5-.5.5h-3c-.27614 0-.5-.22386-.5-.5z" fillRule="evenodd"/>
      <path d="m5 9c0 .27614-.22386.5-.5.5s-.5-.22386-.5-.5.22386-.5.5-.5.5.22386.5.5z"/>
      <path clipRule="evenodd" d="m4.5 8.75c-.13807 0-.25.11193-.25.25s.11193.25.25.25.25-.11193.25-.25-.11193-.25-.25-.25zm-.75.25c0-.41421.33579-.75.75-.75s.75.33579.75.75-.33579.75-.75.75-.75-.33579-.75-.75z" fillRule="evenodd"/>
      <path clipRule="evenodd" d="m6.5 11.5c0-.2761.22386-.5.5-.5h3c.2761 0 .5.2239.5.5s-.2239.5-.5.5h-3c-.27614 0-.5-.2239-.5-.5z" fillRule="evenodd"/>
      <path d="m5 11.5c0 .2761-.22386.5-.5.5s-.5-.2239-.5-.5.22386-.5.5-.5.5.2239.5.5z"/>
      <g clipRule="evenodd" fillRule="evenodd">
        <path d="m4.5 11.25c-.13807 0-.25.1119-.25.25s.11193.25.25.25.25-.1119.25-.25-.11193-.25-.25-.25zm-.75.25c0-.4142.33579-.75.75-.75s.75.3358.75.75-.33579.75-.75.75-.75-.3358-.75-.75z"/>
        <path d="m9.5 2.5c0-.27614.22386-.5.5-.5h.5c1.1046 0 2 .89543 2 2v6c0 .2761-.2239.5-.5.5s-.5-.2239-.5-.5v-6c0-.55228-.4477-1-1-1h-.5c-.27614 0-.5-.22386-.5-.5z"/>
        <path d="m3.5 3c-.55228 0-1 .44772-1 1v9c0 .5523.44772 1 1 1h3.5c.27614 0 .5.2239.5.5s-.22386.5-.5.5h-3.5c-1.10457 0-2-.8954-2-2v-9c0-1.10457.89543-2 2-2h.5c.27614 0 .5.22386.5.5s-.22386.5-.5.5z"/>
        <path d="m14.9868 10.1159c.2121.1768.2408.4921.064.7042l-3.3334 4c-.1635.1963-.4488.2377-.6614.0959l-2.00002-1.3333c-.22976-.1532-.29185-.4636-.13867-.6934.15317-.2297.46361-.2918.69337-.1387l1.62622 1.0842 3.0457-3.6549c.1767-.21213.492-.24079.7042-.064z"/>
      </g>
    </g>
  </svg>
);

const AcquistiIcon = ({ size = 28 }) => (
  <svg fill="none" width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="grad-acquisti" x1="0" x2="0" y1="0" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#e100ff"/>
        <stop offset="1" stopColor="#7f00ff"/>
      </linearGradient>
    </defs>
    <g clipRule="evenodd" fill="url(#grad-acquisti)" fillRule="evenodd">
      <path d="m0 1c0-.552285.447715-1 1-1 1.60652 0 3.04169 1.2815 3.19483 2.89804l1.40028 12.50246c.14959 1.496 1.42581 2.6995 3.00489 2.6995h10.4c.5523 0 1 .4477 1 1s-.4477 1-1 1h-10.4c-2.61874 0-4.7411-1.9931-4.99441-4.4943l-1.39938-12.4944-.0021-.02076c-.05256-.57809-.61442-1.09054-1.20411-1.09054-.552285 0-1-.44772-1-1z"/>
      <path d="m22.0676 11.0365c.5322.1478.8437.699.6959 1.2311-.6192 2.2292-2.6105 3.7324-4.8635 3.7324h-13.3c-.55228 0-1-.4477-1-1s.44772-1 1-1h13.3c1.347 0 2.5557-.8968 2.9365-2.2676.1478-.5322.699-.8437 1.2311-.6959z"/>
      <path d="m2.4 5c0-.55228.44772-1 1-1h5.8c.55228 0 1 .44772 1 1s-.44772 1-1 1h-5.8c-.55228 0-1-.44772-1-1z"/>
      <path d="m19 20c-.5523 0-1 .4477-1 1s.4477 1 1 1 1-.4477 1-1-.4477-1-1-1zm-3 1c0-1.6569 1.3431-3 3-3s3 1.3431 3 3-1.3431 3-3 3-3-1.3431-3-3z"/>
      <path d="m9 20c-.55228 0-1 .4477-1 1s.44772 1 1 1 1-.4477 1-1-.44772-1-1-1zm-3 1c0-1.6569 1.34315-3 3-3 1.6569 0 3 1.3431 3 3s-1.3431 3-3 3c-1.65685 0-3-1.3431-3-3z"/>
      <path d="m23.7071 2.29289c.3905.39053.3905 1.02369 0 1.41422l-6 6c-.3905.39049-1.0237.39049-1.4142 0l-3-3c-.3905-.39053-.3905-1.02369 0-1.41422.3905-.39052 1.0237-.39052 1.4142 0l2.2929 2.2929 5.2929-5.2929c.3905-.39052 1.0237-.39052 1.4142 0z"/>
    </g>
  </svg>
);

const PersonaleIcon = ({ size = 28 }) => (
  <svg fill="none" width={size} height={size} viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="grad-personale" x1="1" x2="1" y1="2" y2="14" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#e100ff"/>
        <stop offset="1" stopColor="#7f00ff"/>
      </linearGradient>
    </defs>
    <g clipRule="evenodd" fill="url(#grad-personale)" fillRule="evenodd">
      <path d="m10 3.5c0-.27614.2239-.5.5-.5 1.3807 0 2.5 1.11929 2.5 2.5s-1.1193 2.5-2.5 2.5c-.2761 0-.5-.22386-.5-.5s.2239-.5.5-.5c.8284 0 1.5-.67157 1.5-1.5s-.6716-1.5-1.5-1.5c-.2761 0-.5-.22386-.5-.5z"/>
      <path d="m12.5003 8.24206c.14-.09373.3178-.11067.4729-.04508.8965.379 1.5268 1.26687 1.5268 2.30302v1c0 .8284-.6716 1.5-1.5 1.5h-.5c-.2761 0-.5-.2239-.5-.5s.2239-.5.5-.5h.5c.2761 0 .5-.2239.5-.5v-1c0-.53191-.2769-.99994-.6959-1.26635-.0707.04071-.1371.07361-.2091.10176-.0686.02683-.1363.04703-.1981.06541-.0054.00161-.0109.00325-.0165.0049-.061.01814-.1312.03901-.2257.06975-.2626.08543-.5447-.05818-.6302-.32078-.0854-.26259.0582-.54472.3208-.63016.107-.03482.1881-.05892.2498-.07724.0057-.00169.0112-.00333.0165-.00492.0666-.01982.0953-.02896.1192-.0383.0362-.01415.0804-.03534.2695-.16201z"/>
      <path d="m6 3c-1.10457 0-2 .89543-2 2s.89543 2 2 2 2-.89543 2-2-.89543-2-2-2zm-3 2c0-1.65685 1.34315-3 3-3s3 1.34315 3 3-1.34315 3-3 3-3-1.34315-3-3z"/>
      <path d="m3.19272 8.56497c.29557-.16818.65635-.17462.95774-.0171.55206.28853 1.18022.45213 1.84954.45213s1.29748-.1636 1.84954-.45213c.30139-.15752.66217-.15108.95774.0171 1.30742.74389 2.19272 2.15213 2.19272 3.76833 0 .9205-.7462 1.6667-1.66667 1.6667h-6.66666c-.92048 0-1.66667-.7462-1.66667-1.6667 0-1.6162.88534-3.02444 2.19272-3.76833zm5.12002.86916c1.00768.57337 1.68726 1.65687 1.68726 2.89917 0 .3682-.29848.6667-.66667.6667h-6.66666c-.36819 0-.66667-.2985-.66667-.6667 0-1.2423.67958-2.3258 1.68726-2.89917.69161.36147 1.47831.56587 2.31274.56587s1.62113-.2044 2.31274-.56587z"/>
    </g>
  </svg>
);

// ─── Custom "Perché Yollo" Icons ───
const SmartphoneCustomIcon = ({ size = 36 }) => (
  <svg fill="none" width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="grad-phone" gradientUnits="userSpaceOnUse" x1="9.5" x2="48" y1="2" y2="52"><stop offset="0" stopColor="#ff00dc"/><stop offset="1" stopColor="#9900ff"/></linearGradient></defs>
    <g fill="url(#grad-phone)">
      <path d="m27 54c1.1046 0 2-.8954 2-2s-.8954-2-2-2h-8c-1.1046 0-2 .8954-2 2s.8954 2 2 2z"/>
      <path d="m39 28c1.1046 0 2-.8954 2-2s-.8954-2-2-2-2 .8954-2 2 .8954 2 2 2z"/>
      <path d="m45 28c1.1046 0 2-.8954 2-2s-.8954-2-2-2-2 .8954-2 2 .8954 2 2 2z"/>
      <path d="m53 26c0 1.1046-.8954 2-2 2s-2-.8954-2-2 .8954-2 2-2 2 .8954 2 2z"/>
      <path clipRule="evenodd" d="m2 10c0-4.41828 3.58172-8 8-8h26c4.0908 0 7.4644 3.07041 7.9421 7.03238.3499-.02148.7026-.03238 1.0579-.03238 9.3888 0 17 7.6112 17 17s-7.6112 17-17 17l-1-.0001v11.0001c0 4.4183-3.5817 8-8 8h-26c-4.41828 0-8-3.5817-8-8zm38 32.9989c-1.9043-.0005-3.8081-.0007-5.7124.0011-.5308.0005-1.04-.21-1.4154-.5851-.3755-.3752-.5865-.8841-.5865-1.4149v-3.715c-2.6646-3.0003-4.2857-6.9551-4.2857-11.285 0-7.6459 5.0476-14.1129 11.9923-16.25043-.1293-2.09248-1.8673-3.74957-3.9923-3.74957h-26c-2.20914 0-4 1.79086-4 4v44c0 2.2091 1.79086 4 4 4h26c2.2091 0 4-1.7909 4-4zm-8-16.9989c0-7.1797 5.8203-13 13-13s13 5.8203 13 13-5.8203 13-13 13c-2.9026 0-5.809-.0022-8.7143-.0012v-2.501c0-.5234-.2052-1.0259-.5715-1.3998-2.2996-2.3467-3.7142-5.5552-3.7142-9.098z" fillRule="evenodd"/>
    </g>
  </svg>
);

const SempreRaggiungibileIcon = ({ size = 36 }) => (
  <svg fill="none" width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="grad-cloud" gradientUnits="userSpaceOnUse" x1="0" x2="0" y1="0" y2="24"><stop offset="0" stopColor="#e100ff"/><stop offset="1" stopColor="#7f00ff"/></linearGradient></defs>
    <g clipRule="evenodd" fill="url(#grad-cloud)" fillRule="evenodd">
      <path d="m3.1222 6.77845c.60197-3.83983 3.89415-6.77845 7.8778-6.77845 3.7098 0 6.8199 2.54849 7.7162 6h.2838c2.7523 0 5 2.24771 5 5 0 2.7523-2.2477 5-5 5h-15.5c-1.95228 0-3.5-1.5477-3.5-3.5 0-1.8012 1.31749-3.25809 3.05466-3.47273-.03692-.33048-.05466-.67327-.05466-1.02727 0-.01349-.00003-.0279-.00006-.04317-.00051-.25298-.00151-.74144.12226-1.17838zm7.8778-4.77845c-2.99009 0-5.48078 2.2225-5.91063 5.14549-.00853.058-.02214.11513-.04068.17074-.03836.11507-.04869.33708-.04869.68377 0 .71964.09031 1.30861.24869 1.78375.11977.35935.02625.75555-.24158 1.02335s-.664.3613-1.02333.2416c-.1344-.0448-.19296-.0487-.48378-.0487-.84772 0-1.5.6523-1.5 1.5s.65228 1.5 1.5 1.5h15.5c1.6477 0 3-1.3523 3-3 0-1.64772-1.3523-3-3-3h-1.1c-.4961 0-.9172-.3637-.9894-.85451-.4298-2.92299-2.9205-5.14549-5.9106-5.14549z"/>
      <path d="m12 20c-.5523 0-1 .4477-1 1s.4477 1 1 1 1-.4477 1-1-.4477-1-1-1zm-3 1c0-1.6569 1.3431-3 3-3s3 1.3431 3 3-1.3431 3-3 3-3-1.3431-3-3z"/>
      <path d="m0 21c0-.5523.447715-1 1-1h9c.5523 0 1 .4477 1 1s-.4477 1-1 1h-9c-.552285 0-1-.4477-1-1z"/>
      <path d="m13 21c0-.5523.4477-1 1-1h9c.5523 0 1 .4477 1 1s-.4477 1-1 1h-9c-.5523 0-1-.4477-1-1z"/>
      <path d="m12 14c.5523 0 1 .4477 1 1v4c0 .5523-.4477 1-1 1s-1-.4477-1-1v-4c0-.5523.4477-1 1-1z"/>
    </g>
  </svg>
);

const DatiTempoRealeIcon = ({ size = 36 }) => (
  <svg fill="none" width={size} height={size} viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="grad-clock" gradientUnits="userSpaceOnUse" x1="1" x2="1" y1="1" y2="15"><stop offset="0" stopColor="#e100ff"/><stop offset="1" stopColor="#7f00ff"/></linearGradient></defs>
    <g clipRule="evenodd" fill="url(#grad-clock)" fillRule="evenodd">
      <path d="m8 2c-3.31363 0-6 2.68495-6 6 0 3.3151 2.68637 6 6 6 3.3136 0 6-2.6849 6-6 0-3.31505-2.6864-6-6-6zm-7 6c0-3.8675 3.13425-7 7-7 3.8658 0 7 3.1325 7 7s-3.1342 7-7 7c-3.86575 0-7-3.1325-7-7z"/>
      <path d="m8 3c.27614 0 .5.22386.5.5v4.5c0 .13261-.05268.25979-.14645.35355l-1.5 1.5c-.19526.19525-.51184.19525-.7071 0-.19527-.19526-.19527-.51184 0-.7071l1.35355-1.35356v-4.29289c0-.27614.22386-.5.5-.5z"/>
    </g>
  </svg>
);

const MultiLocaleIcon = ({ size = 36 }) => (
  <svg fill="none" width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="grad-store" gradientUnits="userSpaceOnUse" x1="0" x2="0" y1="0" y2="24"><stop offset="0" stopColor="#e100ff"/><stop offset="1" stopColor="#7f00ff"/></linearGradient></defs>
    <g clipRule="evenodd" fill="url(#grad-store)" fillRule="evenodd">
      <path d="m3 2c-.54772 0-1 .45228-1 1v.29999c0 1.53018 1.23458 2.70001 2.7 2.70001 1.42984 0 2.6-1.13425 2.6-2.70001v-1.29999zm-3 1c0-1.65228 1.34772-3 3-3h5.3c.55229 0 1 .447715 1 1v2.29999c0 2.63424-2.02984 4.70001-4.6 4.70001-2.53459 0-4.7-2.03019-4.7-4.70001z"/>
      <path d="m7.3 1c0-.552285.44772-1 1-1h7.3c.5523 0 1 .447715 1 1v2.27758c.1146 2.70308-2.0833 4.72242-4.6 4.72242-2.53459 0-4.7-2.03019-4.7-4.70001zm2 1v1.29999c0 1.53018 1.2346 2.70001 2.7 2.70001 1.4755 0 2.6718-1.16815 2.6011-2.65245-.0007-.01584-.0011-.0317-.0011-.04756v-1.29999z"/>
      <path d="m14.6 1c0-.552285.4477-1 1-1h5.3c1.6523 0 3 1.34772 3 3v.27752c.1144 2.68526-1.965 4.72248-4.6 4.72248-2.5346 0-4.7-2.03019-4.7-4.70001zm2 1v1.29999c0 1.53018 1.2346 2.70001 2.7 2.70001 1.5568 0 2.6727-1.1501 2.6011-2.65245-.0007-.01584-.0011-.0317-.0011-.04756v-.29999c0-.54772-.4523-1-1-1z"/>
      <path d="m1.56789 14.5679c.38778-.3878.90486-.5679 1.43211-.5679h18c.5272 0 1.0443.1801 1.4321.5679s.5679.9049.5679 1.4321v5c0 1.6523-1.3477 3-3 3h-16c-1.65228 0-3-1.3477-3-3v-5c0-.5272.18011-1.0443.56789-1.4321zm19.43211 1.4321h-18v5c0 .5477.45228 1 1 1h16c.5477 0 1-.4523 1-1z"/>
      <path d="m4 5.89999c.55228 0 1 .44772 1 1v8.10001c0 .5523-.44772 1-1 1s-1-.4477-1-1v-8.10001c0-.55228.44772-1 1-1z"/>
      <path d="m20 5.89999c.5523 0 1 .44772 1 1v8.10001c0 .5523-.4477 1-1 1s-1-.4477-1-1v-8.10001c0-.55228.4477-1 1-1z"/>
      <path d="m6 19c0-.5523.44772-1 1-1h5c.5523 0 1 .4477 1 1s-.4477 1-1 1h-5c-.55228 0-1-.4477-1-1z"/>
      <path d="m15 19c0-.5523.4477-1 1-1h1c.5523 0 1 .4477 1 1s-.4477 1-1 1h-1c-.5523 0-1-.4477-1-1z"/>
    </g>
  </svg>
);
const FEATURE_DATA = {
  haccp: {
    icon: HaccpIcon, color: "#6C63FF",
    title: "HACCP & Operatività",
    desc: "Monitora temperature, gestisci piani di pulizia, traccia lotti e ricevi alert automatici. Segnalazioni, compiti e calendario — tutto in un unico modulo.",
    features: ["Controllo temperature in tempo reale", "Piano pulizie digitale", "Tracciabilità lotti completa", "Alert automatici e notifiche", "Bacheca compiti operatori", "Segnalazioni e calendario"]
  },
  acquisti: {
    icon: AcquistiIcon, color: "#F59E0B",
    title: "Acquisti & Fornitori",
    desc: "Fornitori, materie prime, ricette con costi, fatture elettroniche. Il cuore economico del tuo ristorante in un'unica piattaforma.",
    features: ["Anagrafica fornitori completa", "Materie prime con categorie e costi", "Ricette e sotto-ricette con food cost", "Importazione fatture da provider", "Storico fatture e carichi", "Alert scorte automatico"]
  },
  personale: {
    icon: PersonaleIcon, color: "#10B981",
    title: "Personale",
    desc: "Turni automatici, presenze giornaliere, richieste ferie. La gestione dei tuoi dipendenti non è mai stata così semplice.",
    features: ["Generazione turni automatica", "Presenze con timbratura", "Ferie & Permessi", "Schede personale dipendenti", "Calendario condiviso", "Vista operatore da smartphone"]
  }
};

// ─── Testimonials Data ───
const TESTIMONIALS = [
  {
    name: "Marco Bianchi",
    role: "Proprietario",
    restaurant: "Trattoria Da Marco",
    city: "Roma",
    text: "Da quando usiamo Yollo, la gestione HACCP non e' piu' un incubo. I ragazzi in cucina compilano tutto dallo smartphone in 30 secondi. Prima ci volevano 20 minuti di carta ogni sera.",
    rating: 5,
    avatar: "MB",
    color: "#6C63FF"
  },
  {
    name: "Giulia Rossi",
    role: "Direttrice",
    restaurant: "Osteria Bella Vita",
    city: "Milano",
    text: "Il modulo acquisti ci ha fatto risparmiare il 15% sui costi food. Finalmente vedo il food cost reale di ogni piatto e posso ottimizzare il menu in modo intelligente.",
    rating: 5,
    avatar: "GR",
    color: "#F59E0B"
  },
  {
    name: "Antonio Ferrara",
    role: "Chef & Titolare",
    restaurant: "Pizzeria Napoli Centrale",
    city: "Napoli",
    text: "Gestisco 3 locali con Yollo. I turni automatici e le presenze digitali mi hanno liberato 10 ore a settimana. Il bot Telegram poi e' una magia: chiedo e lui risponde!",
    rating: 5,
    avatar: "AF",
    color: "#10B981"
  }
];

// ─── FAQ Data ───
const FAQ_DATA = [
  {
    q: "Quanto tempo ci vuole per iniziare a usare Yollo?",
    a: "Meno di 3 minuti! Ti registri, scegli il tuo pacchetto e sei subito operativo. Non servono installazioni, configurazioni complesse o formazione tecnica. L'interfaccia ti guida passo passo."
  },
  {
    q: "Posso provare Yollo gratuitamente?",
    a: "Assolutamente si. Offriamo un periodo di prova gratuita che ti permette di esplorare tutte le funzionalita senza impegno e senza carta di credito. Al termine decidi tu se continuare."
  },
  {
    q: "I miei dati sono al sicuro?",
    a: "La sicurezza e' la nostra priorita. Ogni account ha i propri dati completamente isolati dagli altri. Utilizziamo crittografia di livello bancario e backup automatici giornalieri."
  },
  {
    q: "Posso usare Yollo su smartphone?",
    a: "Assolutamente si! Yollo e' progettato mobile-first: i tuoi dipendenti possono fare tutto direttamente dallo smartphone, dalla registrazione delle temperature alla timbratura presenze, senza bisogno di tablet o computer dedicati."
  },
  {
    q: "Come funziona la gestione multi-locale?",
    a: "Con il servizio Multi-Locale puoi gestire tutte le tue sedi da un unico account. Ogni locale ha i propri dati, operatori e configurazioni, ma tu hai una visione centralizzata di tutto."
  },
  {
    q: "Come funziona l'abbonamento e la durata minima?",
    a: "L'abbonamento minimo e' annuale. Puoi fare upgrade del tuo piano in qualsiasi momento. Al termine del periodo annuale, decidi se rinnovare. Non ci sono rinnovi automatici nascosti: ti avvisiamo sempre prima della scadenza."
  }
];

// ─── FAQ Accordion Item (re-export del componente condiviso) ───
const LandingPage = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [comboRules, setComboRules] = useState([]);
  const [addons, setAddons] = useState({});
  const [ivaPct, setIvaPct] = useState(22);
  const [trialInfo, setTrialInfo] = useState({ attivo: false, giorni: 0 });
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [activeScreen, setActiveScreen] = useState(0);

  useEffect(() => {
    axios.get(`${API}/superadmin/public-pricing-v2`).then(res => {
      setPackages(res.data.pacchetti || []);
      setComboRules(res.data.combo_rules || []);
      setAddons(res.data.addons || {});
      setIvaPct(res.data.iva_percentuale || 22);
      setTrialInfo({ attivo: res.data.trial?.attivo || false, giorni: res.data.trial?.giorni || 0 });
    }).catch(() => {
      axios.get(`${API}/register/packages`).then(res => {
        setPackages(res.data.packages || []);
        setTrialInfo({ attivo: res.data.trial_attivo || false, giorni: res.data.trial_giorni || 0 });
      }).catch(() => {});
    });

    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const allPackages = packages;

  // CTA dinamica in base al trial
  const hasTrial = trialInfo.attivo && (trialInfo.giorni || 0) > 0;
  const ctaPrimary = hasTrial ? "Prova Gratis" : "Inizia subito";
  const ctaHero = hasTrial ? "Inizia gratis" : "Inizia subito";
  const ctaPackage = hasTrial ? "Inizia ora" : "Inizia subito";

  return (
    <div className="min-h-screen overflow-x-hidden relative" style={{ background: "#FAFAFE" }}>

      {/* Blob viola/lilla animati */}
      <div className="fixed inset-0 -z-0 overflow-hidden pointer-events-none">
        <div className="lp-blob lp-blob-1" />
        <div className="lp-blob lp-blob-2" />
        <div className="lp-blob lp-blob-3" />
        <div className="lp-blob lp-blob-4" />
      </div>
      {/* ═══════ HEADER ═══════ */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-gray-200/50 translate-y-0 opacity-100" : "sm:bg-transparent sm:translate-y-0 sm:opacity-100 -translate-y-full opacity-0 pointer-events-none sm:pointer-events-auto"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between transition-all duration-700" style={{ paddingTop: scrolled ? "6px" : "16px", paddingBottom: scrolled ? "6px" : "16px" }}>
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center cursor-pointer flex-shrink-0" data-testid="header-logo-btn">
            <img src="/yollo-logo.png" alt="Yollo" className="rounded-xl transition-all duration-700" style={{ width: scrolled ? "44px" : "140px", height: scrolled ? "44px" : "140px" }} />
          </button>
          <nav className="hidden md:flex items-center gap-1">
            {[
              { label: "Funzionalità", id: "features" },
              { label: "Scopri Yollo", id: "scopri" },
              { label: "Parla con l'AI", id: "ai-chat" },
              { label: "Testimonianze", id: "testimonianze" },
              { label: "Prezzi", id: "pricing" },
              { label: "FAQ", id: "faq" },
            ].map((item) => (
              <button key={item.id} onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-[#6C63FF] rounded-lg hover:bg-[#6C63FF08] transition-all duration-200"
                data-testid={`nav-${item.id}`}>
                {item.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="ghost" onClick={() => navigate("/login")} className="text-gray-600 font-semibold rounded-xl" data-testid="header-login-btn">
              Accedi
            </Button>
            <Button onClick={() => navigate("/register")}
              className="rounded-xl font-bold text-white px-5 bg-[#1A1A2E] hover:bg-white hover:text-[#6C63FF] hover:border hover:border-[#1A1A2E] transition-all duration-300"
              data-testid="header-register-btn">
              {ctaPrimary}
            </Button>
          </div>
        </div>
      </header>

      {/* ═══════ HERO ═══════ */}
      <section className="relative pt-24 pb-20 sm:pt-36 sm:pb-28 overflow-hidden">
        {/* Animated gradient blobs */}
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none animate-blob" style={{ background: "radial-gradient(circle, #6C63FF30, transparent 70%)" }} />
        <div className="absolute top-40 right-1/4 w-[400px] h-[400px] rounded-full opacity-20 pointer-events-none animate-blob animation-delay-2000" style={{ background: "radial-gradient(circle, #FF658430, transparent 70%)" }} />
        <div className="absolute bottom-0 left-1/2 w-[600px] h-[300px] rounded-full opacity-15 pointer-events-none animate-blob animation-delay-4000" style={{ background: "radial-gradient(circle, #10B98130, transparent 70%)" }} />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          {/* Accedi fisso in alto a sinistra su mobile (senza scroll) */}
          <div className="sm:hidden fixed top-4 right-4 z-40">
            <button
              onClick={() => navigate("/login")}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-md border border-gray-200/60 text-gray-600 hover:text-indigo-600 transition-colors"
              data-testid="hero-mobile-login-btn"
              aria-label="Accedi"
            >
              <Lock className="h-5 w-5" />
            </button>
          </div>

          {/* Logo centrato visibile solo su mobile (l'header è nascosto) */}
          <div className="sm:hidden flex justify-center mb-10">
            <img src="/yollo-logo.png" alt="Yollo" className="rounded-2xl w-56 h-56" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[1.1] animate-fade-in-up" style={{ color: "#1A1A2E" }}>
            Più <span className="hero-gradient-text">controllo</span><br />
            sui tuoi locali.<br />
            Più tempo per<br />
            farli <span className="hero-gradient-text">crescere</span>.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-800 max-w-2xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
            Smetti di rincorrere fogli, gruppi WhatsApp e tabelle Excel.
            <br className="hidden sm:block" />
            HACCP, turni, presenze, acquisti e cassa — tutto in un'unica piattaforma, semplice da usare per te e per il tuo team.
          </p>
          <div className="mt-10 flex flex-col items-center gap-3 animate-fade-in-up animation-delay-400">
            <Button onClick={() => navigate("/register")}
              className="h-14 px-8 text-base font-bold rounded-2xl text-white bg-[#1A1A2E] hover:bg-[#6C63FF] transition-all duration-300 hover:scale-105 active:scale-95 shadow-[0_4px_20px_rgba(26,26,46,0.25)] hover:shadow-[0_4px_20px_rgba(108,99,255,0.35)]"
              data-testid="hero-cta-btn">
              {ctaHero} <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            {hasTrial && (
              <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold animate-fade-in-down">
                <Sparkles className="h-4 w-4" /> {trialInfo.giorni} giorni di prova gratuita
              </div>
            )}
          </div>
        </div>

      </section>

      {/* ═══════ SOCIAL PROOF — Bold SaaS style ═══════ */}
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: 100, suffix: "%", title: "Digitale", desc: "Zero carta, zero complicazioni. Tutto il tuo locale in cloud." },
              { value: 24, suffix: "/7", title: "Sempre attivo", desc: "Accedi ai tuoi dati in qualsiasi momento, da qualsiasi dispositivo." },
              { value: 5, suffix: "+", title: "Moduli integrati", desc: "HACCP, acquisti, personale, ricette, turni e molto altro." },
              { value: 0, suffix: "", title: "Setup necessario", desc: "Ti registri e sei operativo. Nessuna configurazione, nessun tecnico." },
            ].map((item, i) => (
              <RevealSection key={i} delay={i * 80} className="h-full">
                <div className="h-full rounded-3xl p-6 sm:p-8 bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-gray-200/50 hover:-translate-y-1 transition-all duration-300 flex flex-col">
                  <p className="text-5xl sm:text-6xl font-black text-gray-900 tracking-tight leading-none"><Counter end={item.value} suffix={item.suffix} /></p>
                  <p className="text-lg font-bold text-gray-800 mt-3">{item.title}</p>
                  <p className="text-sm text-black mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FEATURES ═══════ */}
      <section className="py-20 sm:py-28" id="features">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <RevealSection>
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#6C63FF", backgroundColor: "#6C63FF15" }}>Funzionalità</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">Tutto quello che ti serve,<br />in un unico posto</h2>
              <p className="mt-3 text-black max-w-lg mx-auto">Tre moduli pensati per coprire ogni aspetto della gestione del tuo locale.</p>
            </div>
          </RevealSection>

          <div className="space-y-6">
            {Object.entries(FEATURE_DATA).map(([key, data], idx) => (
              <RevealSection key={key} delay={idx * 120}>
                <div className="rounded-[28px] p-8 sm:p-10 transition-all duration-300 hover:shadow-xl" style={{ background: `linear-gradient(135deg, ${data.color}08, ${data.color}04)`, border: `1px solid ${data.color}15` }}>
                  <div className="flex flex-col lg:flex-row gap-8 items-start">
                    {/* Left: Icon + Title + Desc */}
                    <div className="flex items-start gap-5 flex-1">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${data.color}12` }}>
                        <data.icon size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">{data.title}</h3>
                        <p className="text-black leading-relaxed text-sm sm:text-base">{data.desc}</p>
                      </div>
                    </div>
                    {/* Right: Feature list */}
                    <div className="lg:w-[340px] flex-shrink-0 grid grid-cols-1 gap-2 pl-0 lg:pl-4">
                      {data.features.map((f, fi) => (
                        <div key={fi} className="flex items-center gap-2.5">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${data.color}20` }}>
                            <Check className="h-3 w-3" style={{ color: data.color }} />
                          </div>
                          <span className="text-sm text-black">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ APP SCREENSHOTS GALLERY ═══════ */}
      <section id="scopri" className="py-20 sm:py-28 overflow-hidden" style={{ background: "linear-gradient(180deg, #FAFAFE, #F0EFFE)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <RevealSection>
            <div className="text-center mb-14">
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#8B5CF6", backgroundColor: "#8B5CF615" }}>Scopri Yollo</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">Ogni area del tuo locale,<br />a portata di smartphone</h2>
              <p className="mt-3 text-black max-w-lg mx-auto">Ogni persona vede solo quello che gli serve. E tu, anche dal divano, puoi chiedere su Telegram com'è andata la giornata e ricevere la risposta in secondi.</p>
            </div>
          </RevealSection>

          {/* Screenshot tabs */}
          <RevealSection delay={100}>
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {[
                { label: "Dashboard", icon: BarChart3 },
                { label: "Andamento", icon: TrendingUp },
                { label: "Fornitori", icon: ShoppingCart },
                { label: "Turni", icon: Calendar },
                { label: "Compiti", icon: UserCheck },
                { label: "Documenti", icon: FolderOpen },
              ].map((tab, i) => (
                <button key={i} onClick={() => setActiveScreen(i)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeScreen === i ? "text-white shadow-lg" : "bg-white text-gray-500 border border-gray-200 hover:border-gray-300"}`}
                  style={activeScreen === i ? { background: "linear-gradient(135deg, #6C63FF, #8B85FF)" } : {}}
                  data-testid={`screenshot-tab-${i}`}>
                  <tab.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </RevealSection>

          {/* Screenshot display */}
          <RevealSection delay={200}>
            <div className="relative max-w-5xl mx-auto">
              <div className="absolute -inset-4 rounded-[36px] opacity-50" style={{ background: "linear-gradient(135deg, #6C63FF20, #FF658420, #10B98120)", filter: "blur(40px)" }} />
              <div className="relative rounded-[24px] overflow-hidden border border-gray-200/60 shadow-2xl bg-white">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="px-4 py-1 rounded-lg bg-white border border-gray-200 text-xs text-gray-400 font-mono">www.yollo.me</div>
                  </div>
                </div>
                {[
                  { src: "/screenshots/dashboard-preview.png", alt: "Dashboard gestionale ristorante Yollo con notifiche e alert", caption: "Panoramica completa: notifiche, alert e centro di controllo" },
                  { src: "/screenshots/charts-preview.png", alt: "Grafici andamento incassi e uscite ristorante", caption: "Andamento entrate, uscite e margini con grafici interattivi" },
                  { src: "/screenshots/fornitori-preview.png", alt: "Gestione fornitori e acquisti per ristoranti", caption: "Anagrafica fornitori, trattative e confronto prezzi" },
                  { src: "/screenshots/turni-preview.png", alt: "Software gestione turni e presenze personale ristorante", caption: "Turni automatici, presenze e calendario condiviso" },
                  { src: "/screenshots/compiti-preview.png", alt: "App compiti e checklist operatori ristorazione", caption: "Badge operatore, compiti assegnati e checklist giornaliere" },
                  { src: "/screenshots/documenti-preview.png", alt: "Archivio documenti digitale per locali e ristoranti", caption: "Documenti della societa e dei dipendenti sempre online" },
                ].map((screen, i) => (
                  <div key={i} className={`transition-opacity duration-500 ${activeScreen === i ? "block" : "hidden"}`}>
                    <img src={screen.src} alt={screen.alt} className="w-full" loading="lazy" />
                  </div>
                ))}
              </div>
              {/* Caption */}
              <div className="text-center mt-5">
                <p className="text-sm text-black font-medium">
                  {[
                    "Panoramica completa: notifiche, alert e centro di controllo",
                    "Andamento entrate, uscite e margini con grafici interattivi",
                    "Anagrafica fornitori, trattative e confronto prezzi",
                    "Turni automatici, presenze e calendario condiviso",
                    "Badge operatore, compiti assegnati e checklist giornaliere",
                    "Documenti della societa e dei dipendenti sempre online e raggiungibili",
                  ][activeScreen]}
                </p>
              </div>
            </div>
          </RevealSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-12 max-w-5xl mx-auto">
            {[
              { title: "100% da smartphone", desc: "I tuoi dipendenti operano direttamente dal loro telefono, senza bisogno di dispositivi aggiuntivi", IconComp: SmartphoneCustomIcon },
              { title: "Sempre raggiungibile", desc: "Documenti, turni, compiti: tutto online e accessibile ovunque, in qualsiasi momento", IconComp: SempreRaggiungibileIcon },
              { title: "Dati in tempo reale", desc: "Dashboard finanziaria e operativa aggiornata istante per istante", IconComp: DatiTempoRealeIcon },
              { title: "Multi-Locale", desc: "Gestisci più locali da un unico account. Ogni sede con i propri dati, operatori e configurazioni.", IconComp: MultiLocaleIcon },
            ].map((item, i) => (
              <RevealSection key={i} delay={300 + i * 100}>
                <div className="text-center p-4">
                  <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: "#6C63FF08" }}>
                    <item.IconComp size={36} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                  <p className="text-sm text-black">{item.desc}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ BOT TELEGRAM ═══════ */}
      <section id="ai-chat" className="py-20 sm:py-28" style={{ background: "linear-gradient(180deg, #F0EFFE, #FAFAFE)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            {/* Left: Info */}
            <RevealSection className="flex-1 max-w-lg">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#0088CC", backgroundColor: "#0088CC15" }}>Bot Telegram</span>
                <h2 className="mt-5 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight leading-tight">
                  Chiedi al tuo locale.<br />
                  <span style={{ color: "#0088CC" }}>Lui ti risponde.</span>
                </h2>
                <p className="mt-4 text-black leading-relaxed text-base">
                  Un messaggio di testo o un vocale su Telegram: tutto quello che serve per avere informazioni in tempo reale sul tuo locale. Dati finanziari, operativi, scorte, turni — il bot AI sa tutto.
                </p>
                <div className="mt-8 space-y-4">
                  {[
                    { text: "Invia un messaggio o un vocale, il bot capisce tutto", icon: Mic, color: "#0088CC" },
                    { text: "Dati finanziari: entrate, uscite, food cost in tempo reale", icon: TrendingUp, color: "#10B981" },
                    { text: "Dati operativi: temperature, scorte, compiti del giorno", icon: Bell, color: "#F59E0B" },
                    { text: "Funziona su qualsiasi smartphone, nessuna app da installare", icon: Smartphone, color: "#8B5CF6" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${item.color}12` }}>
                        <item.icon className="h-4 w-4" style={{ color: item.color }} />
                      </div>
                      <p className="text-sm text-black font-medium">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </RevealSection>

            {/* Right: Telegram Chat Mock */}
            <RevealSection delay={200} className="flex-1 max-w-md w-full">
              <div className="relative">
                <div className="absolute -inset-3 rounded-[36px] opacity-40" style={{ background: "radial-gradient(circle, #0088CC20, transparent 70%)" }} />
                <div className="relative rounded-[28px] overflow-hidden shadow-2xl border border-gray-200/60" style={{ background: "#E8DDD3" }}>
                  {/* Telegram header */}
                  <div className="px-4 py-3 flex items-center gap-3" style={{ background: "linear-gradient(135deg, #0088CC, #0077B5)" }}>
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Bot className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">Yollo Bot</p>
                      <p className="text-white/60 text-xs">online</p>
                    </div>
                  </div>
                  {/* Chat body */}
                  <div className="p-4 space-y-3 min-h-[380px]" data-testid="telegram-chat-mock">
                    {/* User text message */}
                    <div className="flex justify-end">
                      <div className="max-w-[75%] rounded-2xl rounded-tr-md px-4 py-2.5 shadow-sm" style={{ backgroundColor: "#DCF8C6" }}>
                        <p className="text-sm text-gray-800">Qual e' il food cost medio di questa settimana?</p>
                        <p className="text-[10px] text-gray-400 text-right mt-1">10:32</p>
                      </div>
                    </div>
                    {/* Bot response */}
                    <div className="flex justify-start">
                      <div className="max-w-[80%] bg-white rounded-2xl rounded-tl-md px-4 py-2.5 shadow-sm">
                        <p className="text-sm text-gray-800">Il <strong>food cost medio</strong> di questa settimana e'<br/><span className="text-xl font-black" style={{ color: "#10B981" }}>28.4%</span></p>
                        <p className="text-xs text-black mt-1.5">In calo del 2.1% rispetto alla settimana scorsa. I piatti con margine piu' alto sono: Carbonara (72%), Pizza Margherita (68%).</p>
                        <p className="text-[10px] text-gray-400 mt-1">10:32</p>
                      </div>
                    </div>
                    {/* User voice message */}
                    <div className="flex justify-end">
                      <div className="max-w-[70%] rounded-2xl rounded-tr-md px-4 py-2.5 shadow-sm" style={{ backgroundColor: "#DCF8C6" }}>
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#34B7F140" }}>
                            <Mic className="h-4 w-4" style={{ color: "#0088CC" }} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-0.5 h-6">
                              {Array.from({ length: 24 }).map((_, i) => (
                                <div key={i} className="w-[3px] rounded-full" style={{
                                  backgroundColor: "#0088CC",
                                  height: `${Math.max(4, Math.sin(i * 0.7) * 16 + Math.random() * 8 + 6)}px`,
                                  opacity: 0.6 + Math.random() * 0.4
                                }} />
                              ))}
                            </div>
                            <p className="text-[10px] text-black mt-0.5">0:04</p>
                          </div>
                        </div>
                        <p className="text-[10px] text-gray-400 text-right mt-0.5">10:33</p>
                      </div>
                    </div>
                    {/* Bot response to voice */}
                    <div className="flex justify-start">
                      <div className="max-w-[80%] bg-white rounded-2xl rounded-tl-md px-4 py-2.5 shadow-sm">
                        <p className="text-sm text-gray-800">Oggi hai <strong>3 compiti non completati</strong>:</p>
                        <div className="mt-1.5 space-y-1">
                          <div className="flex items-center gap-2 text-xs"><span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" /><span className="text-black">Registrazione temperature frigo 2</span></div>
                          <div className="flex items-center gap-2 text-xs"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" /><span className="text-black">Pulizia area preparazione</span></div>
                          <div className="flex items-center gap-2 text-xs"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" /><span className="text-black">Verifica scorte magazzino</span></div>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-2">10:33</p>
                      </div>
                    </div>
                  </div>
                  {/* Input bar */}
                  <div className="px-3 py-2.5 flex items-center gap-2" style={{ backgroundColor: "#F0F0F0" }}>
                    <div className="flex-1 bg-white rounded-full px-4 py-2.5 text-sm text-gray-400 border border-gray-200">
                      Scrivi un messaggio o invia un vocale...
                    </div>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "#0088CC" }}>
                      <Mic className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* ═══════ PENSATO PER ═══════ */}
      <section id="pensato-per" className="py-14 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <RevealSection>
            <div className="text-center mb-8">
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#6C63FF", backgroundColor: "#6C63FF15" }}>Per chi è Yollo</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-black text-gray-900">Pensato per chi lavora nel food</h2>
            </div>
          </RevealSection>
          
          {/* Compact Bento Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 lg:gap-3">
            {/* Pizzerie - Large */}
            <div className="col-span-2 row-span-2 group">
              <div className="relative h-full min-h-[240px] md:min-h-[280px] rounded-xl overflow-hidden cursor-pointer" data-testid="target-sector-0">
                <img src={`${API}/superadmin/files/yollo-ristoranti/landing/60a0fab8-d9bd-4e42-aa37-976a8f366383.webp`} alt="Pizzerie" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end">
                  <span className="inline-block px-3 py-1 rounded-md text-xl md:text-2xl font-extrabold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>Pizzerie</span>
                </div>
              </div>
            </div>

            {/* Macellerie */}
            <div className="col-span-1 group">
              <div className="relative h-full min-h-[115px] md:min-h-[135px] rounded-xl overflow-hidden cursor-pointer" data-testid="target-sector-1">
                <img src={`${API}/superadmin/files/yollo-ristoranti/landing/0fe1410a-2af7-47cf-8358-706546ff2bb8.webp`} alt="Macellerie" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end">
                  <span className="inline-block px-2.5 py-0.5 rounded-md text-sm md:text-base font-extrabold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>Macellerie</span>
                </div>
              </div>
            </div>

            {/* Pasticcerie */}
            <div className="col-span-1 group">
              <div className="relative h-full min-h-[115px] md:min-h-[135px] rounded-xl overflow-hidden cursor-pointer" data-testid="target-sector-2">
                <img src={`${API}/superadmin/files/yollo-ristoranti/landing/254c985e-8381-498a-aebd-1569bbfe9251.webp`} alt="Pasticcerie" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end">
                  <span className="inline-block px-2.5 py-0.5 rounded-md text-sm md:text-base font-extrabold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>Pasticcerie</span>
                </div>
              </div>
            </div>

            {/* Laboratori */}
            <div className="col-span-1 group">
              <div className="relative h-full min-h-[115px] md:min-h-[135px] rounded-xl overflow-hidden cursor-pointer" data-testid="target-sector-4">
                <img src={`${API}/superadmin/files/yollo-ristoranti/landing/7a6ef9d9-dbe3-4cfd-94cb-0679c45e0427.webp`} alt="Laboratori" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end">
                  <span className="inline-block px-2.5 py-0.5 rounded-md text-sm md:text-base font-extrabold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>Laboratori</span>
                </div>
              </div>
            </div>

            {/* Franchising */}
            <div className="col-span-1 group">
              <div className="relative h-full min-h-[115px] md:min-h-[135px] rounded-xl overflow-hidden cursor-pointer" data-testid="target-sector-5">
                <img src={`${API}/superadmin/files/yollo-ristoranti/landing/9cea7b81-f29d-4023-ba46-6e987a633362.webp`} alt="Franchising" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end">
                  <span className="inline-block px-2.5 py-0.5 rounded-md text-sm md:text-base font-extrabold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>Franchising</span>
                </div>
              </div>
            </div>

            {/* Ristoranti - Wide */}
            <div className="col-span-2 group">
              <div className="relative h-full min-h-[115px] md:min-h-[135px] rounded-xl overflow-hidden cursor-pointer" data-testid="target-sector-3">
                <img src={`${API}/superadmin/files/yollo-ristoranti/landing/82a7450d-6e6c-4bab-9cf5-86135f849fd6.webp`} alt="Ristoranti" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end">
                  <span className="inline-block px-3 py-0.5 rounded-md text-base md:text-lg font-extrabold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>Ristoranti</span>
                </div>
              </div>
            </div>

            {/* Catene */}
            <div className="col-span-1 group">
              <div className="relative h-full min-h-[115px] md:min-h-[135px] rounded-xl overflow-hidden cursor-pointer" data-testid="target-sector-6">
                <img src={`${API}/superadmin/files/yollo-ristoranti/landing/f19d6abe-8ab0-46b8-b208-1340f3ce575a.webp`} alt="Catene" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end">
                  <span className="inline-block px-2.5 py-0.5 rounded-md text-sm md:text-base font-extrabold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>Catene</span>
                </div>
              </div>
            </div>

            {/* E molto altro */}
            <div className="col-span-1 group">
              <div className="relative h-full min-h-[115px] md:min-h-[135px] rounded-xl overflow-hidden cursor-pointer" data-testid="target-sector-7">
                <img src={`${API}/superadmin/files/yollo-ristoranti/landing/1e0c4af9-d38e-43d2-8162-c928ccf6d201.webp`} alt="E molto altro" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end">
                  <span className="inline-block px-2.5 py-0.5 rounded-md text-sm md:text-base font-extrabold text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.8)', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>E molto altro</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ TESTIMONIALS ═══════ */}
      <section id="testimonianze" className="py-20 sm:py-28 overflow-hidden" style={{ background: "linear-gradient(180deg, #FAFAFE, #FAFAFE)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <RevealSection>
            <div className="text-center mb-14">
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#F59E0B", backgroundColor: "#F59E0B15" }}>Testimonianze</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">Chi lo usa, lo ama</h2>
              <p className="mt-3 text-black max-w-lg mx-auto">Ristoratori come te che hanno trasformato la gestione del loro locale.</p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <RevealSection key={i} delay={i * 150}>
                <div className="relative bg-white rounded-[24px] p-6 border border-gray-200/60 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col" data-testid={`testimonial-card-${i}`}>
                  <Quote className="h-8 w-8 mb-4 opacity-15" style={{ color: t.color }} />
                  <p className="text-black leading-relaxed text-sm flex-1 mb-5">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, si) => (
                      <Star key={si} className="h-4 w-4 fill-current" style={{ color: "#F59E0B" }} />
                    ))}
                  </div>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: `linear-gradient(135deg, ${t.color}, ${t.color}CC)` }}>
                      {t.avatar}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{t.name}</p>
                      <p className="text-xs text-black">{t.role} &middot; {t.restaurant}, {t.city}</p>
                    </div>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ PRICING ═══════ */}
      <section className="py-20 sm:py-28" id="pricing" style={{ background: "linear-gradient(180deg, #FAFAFE, #F0EFFE)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <RevealSection>
            <div className="text-center mb-10">
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#6C63FF", backgroundColor: "#6C63FF15" }}>Pricing</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900">Scegli il tuo piano</h2>
              <p className="mt-3 text-black">Componi il piano perfetto per il tuo locale. Abbonamento annuale, pagamento mensile.</p>
              {hasTrial && (
                <div className="mt-4 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold">
                  <Sparkles className="h-4 w-4" /> {trialInfo.giorni} giorni di prova gratuita su tutti i piani
                </div>
              )}
            </div>
          </RevealSection>

          {/* ─── Combo Banner ─── */}
          {comboRules.length > 0 && (
            <RevealSection delay={50}>
              <div className="relative max-w-3xl mx-auto mb-10 overflow-hidden rounded-[24px] border-2 border-indigo-300 shadow-xl" style={{ background: "linear-gradient(135deg, #F5F3FF, #EEF2FF, #FDF2F8)" }}>
                <div className="absolute top-0 right-0 px-5 py-2 rounded-bl-2xl text-sm font-black text-white" style={{ background: "linear-gradient(135deg, #EF4444, #F97316)" }} data-testid="combo-save-badge">
                  RISPARMIA COMBINANDO
                </div>
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="h-5 w-5" style={{ color: "#6C63FF" }} />
                    <h3 className="text-xl font-black text-gray-900">Offerte Combo</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {comboRules.map((rule, idx) => {
                      const somma = allPackages.reduce((s, p) => s + (p.prezzo_mensile_annuale || 0), 0);
                      const risparmio = somma - rule.prezzo_mensile;
                      return (
                        <div key={idx} className="bg-white/80 rounded-2xl p-4 text-center border border-gray-100 shadow-sm">
                          <p className="text-xs font-bold text-indigo-600 uppercase">{rule.label}</p>
                          <div className="mt-2 flex items-baseline justify-center gap-1">
                            <span className="text-3xl font-black" style={{ color: "#6C63FF" }}>{rule.prezzo_mensile}</span>
                            <span className="text-gray-400">/mese</span>
                          </div>
                          {risparmio > 0 && rule.num_pacchetti === 3 && (
                            <p className="text-[11px] text-emerald-600 font-bold mt-1">Risparmi {Math.round(risparmio)}/mese</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-center mt-5">
                    <Button onClick={() => navigate("/register")}
                      className="h-11 px-8 rounded-xl font-bold text-white transition-transform hover:scale-[1.02]"
                      style={{ background: "linear-gradient(135deg, #6C63FF, #8B5CF6)" }}
                      data-testid="combo-cta-btn">
                      {ctaPackage} <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </RevealSection>
          )}

          <p className="text-center text-sm text-black mb-6">Oppure scegli i singoli moduli</p>

          {/* ─── Package Cards ─── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
            {allPackages.map((pkg, idx) => {
              const price = Number(pkg.prezzo_mensile_annuale) || 0;
              const originalPrice = pkg.sconto_percentuale > 0 ? price : null;
              const finalPrice = pkg.sconto_percentuale > 0 ? price * (1 - pkg.sconto_percentuale / 100) : price;
              const colors = ["#6C63FF", "#F59E0B", "#10B981"];
              const color = colors[idx % 3];
              const Icons = [Thermometer, ShoppingCartIcon, Users];
              const IconComp = Icons[idx % 3];

              return (
                <div key={pkg.id} className={`relative bg-white rounded-[28px] overflow-hidden border shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col h-full ${pkg.in_evidenza ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-100'}`}>
                    
                    {/* Immagine pacchetto 16:9 */}
                    {pkg.immagine_url ? (
                      <div className="relative w-full aspect-[16/9] overflow-hidden">
                        <img 
                          src={pkg.immagine_url.startsWith("http") ? pkg.immagine_url : `${API}/superadmin/files/${pkg.immagine_url}`}
                          alt={pkg.nome}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {/* Overlay sfumato */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                        
                        {/* Badge CONSIGLIATO */}
                        {pkg.in_evidenza && (
                          <div className="absolute top-4 left-4 px-4 py-1.5 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900 flex items-center gap-1.5 shadow-lg">
                            <Star className="h-3.5 w-3.5 fill-yellow-900" /> CONSIGLIATO
                          </div>
                        )}
                        {/* Badge SCONTO */}
                        {pkg.sconto_percentuale > 0 && (
                          <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold bg-red-500 text-white shadow-lg">-{pkg.sconto_percentuale}%</div>
                        )}
                      </div>
                    ) : (
                      <div className="p-8 pb-4 relative">
                        {pkg.in_evidenza && (
                          <div className="absolute top-4 left-4 px-4 py-1.5 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900 flex items-center gap-1.5 shadow-sm">
                            <Star className="h-3.5 w-3.5 fill-yellow-900" /> CONSIGLIATO
                          </div>
                        )}
                        {pkg.sconto_percentuale > 0 && (
                          <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold bg-red-500 text-white shadow-sm">-{pkg.sconto_percentuale}%</div>
                        )}
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mt-6" style={{ backgroundColor: `${color}15` }}>
                          <IconComp className="h-7 w-7" style={{ color }} />
                        </div>
                      </div>
                    )}
                    
                    {/* Contenuto card - flex-grow per allineare bottoni */}
                    <div className="flex flex-col flex-grow px-7 pt-6 pb-7">
                      {/* Titolo e descrizione */}
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.nome}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed mb-5 min-h-[48px]">{pkg.descrizione}</p>
                      
                      {/* Prezzo */}
                      <div className="mb-6">
                        <div className="flex items-baseline gap-1">
                          {originalPrice && <span className="text-lg line-through text-gray-300 mr-1">{originalPrice.toFixed(0)}€</span>}
                          <span className="text-4xl font-black text-gray-900">{finalPrice.toFixed(0)}</span>
                          <span className="text-lg text-gray-400 font-medium">€</span>
                        </div>
                        <span className="text-sm text-gray-400">/mese + IVA</span>
                      </div>
                      
                      {/* Features */}
                      <div className="space-y-2.5 mb-8 flex-grow">
                        {(pkg.feature_labels || pkg.features || []).map((f, fi) => {
                          const label = typeof f === "string" ? f : f;
                          return (
                            <div key={fi} className="flex items-center gap-3 text-sm">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                                <Check className="h-3 w-3" style={{ color }} />
                              </div>
                              <span className="text-gray-700">{label}</span>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Bottone - sempre in fondo */}
                      <Button onClick={() => navigate("/register")}
                        className={`w-full h-12 rounded-xl font-bold text-base transition-all hover:scale-[1.02] hover:shadow-lg ${pkg.in_evidenza ? 'text-white shadow-md' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
                        style={pkg.in_evidenza ? { background: `linear-gradient(135deg, ${color}, ${color}DD)` } : {}}>
                        {ctaPackage} <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                </div>
              );
            })}
          </div>

          {/* ─── Add-ons row ─── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {addons.telegram_bot && (
                <div className="bg-white rounded-[20px] p-5 border border-gray-200/60 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                      <Bot className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Bot Telegram AI</h4>
                      <p className="text-xs text-black">Assistente intelligente per il tuo locale</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">1° numero</p>
                      <p className="text-xl font-black text-gray-900">{addons.telegram_bot.prezzo_primo_numero || addons.telegram_bot.prezzo_mensile}<span className="text-sm font-normal text-gray-400">€/mese</span></p>
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">Numeri aggiuntivi</p>
                      <p className="text-xl font-black text-gray-900">{addons.telegram_bot.prezzo_numero_extra}<span className="text-sm font-normal text-gray-400">€/cad.</span></p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-center">+ IVA</p>
                </div>
            )}
            {addons.locale_extra && (
                <div className="bg-white rounded-[20px] p-5 border border-gray-200/60 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Locale Extra</h4>
                      <p className="text-xs text-black">Gestisci più sedi dalla stessa piattaforma</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">Locali inclusi</p>
                      <p className="text-xl font-black text-gray-900">{addons.locale_extra.locali_inclusi ?? 1}<span className="text-sm font-normal text-gray-400"> gratis</span></p>
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">Ogni sede extra</p>
                      <p className="text-xl font-black text-gray-900">{addons.locale_extra.prezzo_extra_annuale || addons.locale_extra.prezzo_mensile}<span className="text-sm font-normal text-gray-400">€/mese</span></p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-center">+ IVA</p>
                </div>
            )}
          </div>

          <p className="text-center text-xs text-gray-400 mt-4">Tutti i prezzi sono IVA ({ivaPct}%) esclusa.</p>
        </div>
      </section>

      {/* ═══════ FAQ ═══════ */}
      <section id="faq" className="py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <RevealSection>
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#6C63FF", backgroundColor: "#6C63FF15" }}>FAQ</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">Domande frequenti</h2>
              <p className="mt-3 text-black">Tutto quello che devi sapere prima di iniziare.</p>
            </div>
          </RevealSection>

          <RevealSection delay={100}>
            <div className="bg-white rounded-[24px] border border-gray-200/60 shadow-md px-6 sm:px-8" data-testid="faq-section">
              {FAQ_DATA.map((faq, i) => (
                <FaqItem key={i} index={i} question={faq.q} answer={faq.a}
                  isOpen={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)} />
              ))}
            </div>
          </RevealSection>

          <RevealSection delay={200}>
            <div className="text-center mt-8">
              <p className="text-sm text-black">Hai altre domande?</p>
              <button onClick={() => navigate("/register")} className="text-sm font-bold mt-1 hover:underline" style={{ color: "#6C63FF" }}>
                {hasTrial ? "Contattaci o inizia la prova gratuita" : "Contattaci o registrati ora"}
              </button>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="border-t border-gray-200 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/yollo-logo.png" alt="Yollo" className="w-10 h-10 rounded-lg" />
            <span className="text-sm text-gray-400">— Scala la tua attività. Senza perdere il controllo.</span>
          </div>
          <div className="flex items-center gap-4 text-sm flex-wrap justify-center">
            <Link to="/software-haccp" className="text-gray-500 hover:text-[#6C63FF] font-medium" data-testid="footer-haccp-link">Software HACCP</Link>
            <span className="text-gray-300 hidden sm:inline">|</span>
            <button onClick={() => navigate("/privacy")} className="text-gray-400 hover:text-gray-600">Privacy Policy</button>
            <button onClick={() => navigate("/cookie-policy")} className="text-gray-400 hover:text-gray-600">Cookie Policy</button>
            <span className="text-gray-300 hidden sm:inline">|</span>
            <button onClick={() => navigate("/login")} className="text-gray-500 hover:text-gray-700 font-medium" data-testid="footer-login-link">Accedi</button>
            <button onClick={() => navigate("/register")} className="font-bold hover:underline" style={{ color: "#6C63FF" }} data-testid="footer-register-link">Registrati</button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">© 2026 Luchapp S.r.l. — Via Tucci 4, 04100 Latina (LT) — P.IVA 03368240598</p>
        </div>
      </footer>

      {/* ═══════ CSS ANIMATIONS ═══════ */}
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes blob { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(30px, -20px) scale(1.05); } 66% { transform: translate(-15px, 15px) scale(0.95); } }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-12px); } }
        .animate-fade-in-up { animation: fadeInUp 0.8s ease-out forwards; }
        .animate-fade-in-down { animation: fadeInDown 0.6s ease-out forwards; }
        .animate-blob { animation: blob 8s ease-in-out infinite; }
        .animate-float { animation: float 4s ease-in-out infinite; }
        .animation-delay-200 { animation-delay: 200ms; }
        .animation-delay-400 { animation-delay: 400ms; }
        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>

      {/* Hide Emergent badge */}
      <style>{`
        #emergent-badge, [id*="emergent"], a[href*="emergent.sh"], [class*="emergent"],
        div[style*="position: fixed"][style*="bottom"][style*="right"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}</style>

      {/* Blob viola/lilla animati su sfondo bianco */}
      <style>{`
        .hero-gradient-text {
          background: linear-gradient(135deg, #6C63FF, #8B85FF, #FF6584, #6C63FF);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: heroGradShift 6s ease-in-out infinite;
        }
        @keyframes heroGradShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .lp-blob {
          position: absolute;
          border-radius: 50%;
          will-change: transform;
        }
        .lp-blob-1 {
          width: 55vw; height: 55vw; max-width: 750px; max-height: 750px;
          top: -5%; left: -10%;
          background: #A78BFA;
          opacity: 0.18;
          filter: blur(130px);
          animation: lpBlob1 18s ease-in-out infinite;
        }
        .lp-blob-2 {
          width: 45vw; height: 45vw; max-width: 600px; max-height: 600px;
          top: 25%; right: -8%;
          background: #C084FC;
          opacity: 0.15;
          filter: blur(120px);
          animation: lpBlob2 22s ease-in-out infinite;
        }
        .lp-blob-3 {
          width: 50vw; height: 50vw; max-width: 700px; max-height: 700px;
          bottom: 15%; left: 20%;
          background: #818CF8;
          opacity: 0.12;
          filter: blur(140px);
          animation: lpBlob3 20s ease-in-out infinite;
        }
        .lp-blob-4 {
          width: 35vw; height: 35vw; max-width: 450px; max-height: 450px;
          top: 55%; right: 25%;
          background: #DDD6FE;
          opacity: 0.22;
          filter: blur(100px);
          animation: lpBlob4 16s ease-in-out infinite;
        }

        @keyframes lpBlob1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(90px, 70px) scale(1.12); }
          50% { transform: translate(-40px, 130px) scale(0.92); }
          75% { transform: translate(70px, -30px) scale(1.08); }
        }
        @keyframes lpBlob2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-80px, 90px) scale(1.15); }
          66% { transform: translate(60px, -70px) scale(0.88); }
        }
        @keyframes lpBlob3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(110px, -60px) scale(1.1); }
          50% { transform: translate(-90px, 80px) scale(0.93); }
          75% { transform: translate(50px, -90px) scale(1.15); }
        }
        @keyframes lpBlob4 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-80px, -100px) scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
